import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useTree } from 'src/queries/tree';
import { useCompany } from 'src/queries/company';
import {
  usePulseQuestion,
  submitSharedPulseReview,
  usePulseReviewsDue
} from 'src/queries/pulse';
import PulseReviewSharedForm from 'src/containers/PulseReviewForm/PulseReviewSharedForm';
import { toast } from 'src/components';
import appUtils from 'src/components/appUtils';
import PULSE_CONSTANTS from 'common/pulseConstants';
import commonPulseUtils from 'common/commonPulseUtils';
import commonDateUtils from 'common/commonDateUtils';

const PulseReviewFromShare = () => {
  const navigate = useNavigate();
  const { questionid } = useParams();

  const loggedUserId = appUtils.getLoggedUserId();

  const {
    data: company,
    isFetching: isFetchingCompany,
    isError: isErrorCompany
  } = useCompany({
    isEnabled: Boolean(loggedUserId)
  });

  const {
    pulseReviews,
    isFetching: isFetchingPulseReviewsDue,
    isError: isErrorPulseReviewsDue
  } = usePulseReviewsDue({
    enabled: Boolean(loggedUserId)
  });

  const existingPulseReview = isEmpty(pulseReviews)
    ? null
    : pulseReviews.find((review) => review.questionId === questionid);

  const {
    data: pulseQuestion,
    isFetching: isFetchingPulse,
    isError: isErrorPulseData
  } = usePulseQuestion({ questionid });

  const {
    mutateAsync: submitPulseReview,
    isLoading: submitPulseReviewLoading
  } = submitSharedPulseReview();

  const isError = isErrorCompany || isErrorPulseData || isErrorPulseReviewsDue;
  const isFetching = isFetchingPulse
    || isFetchingCompany
    || submitPulseReviewLoading
    || (loggedUserId && (isFetchingCompany || isFetchingPulseReviewsDue));
  const isReady = !isFetching && !isError;

  const {
    id: pulseQuestionId,
    text: questionText,
    choices: questionChoices,
    anonymity: questionAnonymity,
    type: questionType
  } = isReady ? pulseQuestion : {};

  const canSubmitPulse = (submitDate, curQuestion) => (curQuestion ? curQuestion.date !== submitDate : true);

  useEffect(() => {
    if (isReady && !existingPulseReview) {
      const reviewedToday = JSON.parse(localStorage.getItem('pulse-shared-answered')) ?? [];
      const submitDate = commonDateUtils.dateToMonthDayYearFormat(new Date());
      const curQuestion = reviewedToday.find(
        (question) => question.id === pulseQuestionId
      );

      const isSubmitValid = canSubmitPulse(submitDate, curQuestion);
      if (!isSubmitValid) {
        toast.show('This question is not available!');
        navigate(appUtils.getHomeRoute());
      }
    }
  }, [isReady, existingPulseReview, navigate, pulseQuestionId]);

  const submit = async (values) => {
    const { comments, choice, anonymous } = values;
    const isFreeTextAnswerType = questionType === PULSE_CONSTANTS.TYPES.FREE;

    const data = {
      pulseQuestionId,
      answer: isFreeTextAnswerType ? null : choice.label,
      score: isFreeTextAnswerType ? null : choice.score,
      comments,
      anonymous: loggedUserId
        ? commonPulseUtils.getAnonymous(questionAnonymity, anonymous.value)
        : true,
      companyid: company?.id
    };

    if (existingPulseReview) {
      data.pulseId = existingPulseReview.id;
      const result = await submitPulseReview(data);
      if (!result || !result.success) {
        toast.error('Uh oh, we ran into an issue. Please try again later!');
        return false;
      }
      toast.show('Answer submitted');
      navigate(appUtils.getHomeRoute());
      return true;
    }

    if (!existingPulseReview) {
      const reviewedToday = JSON.parse(localStorage.getItem('pulse-shared-answered')) ?? [];
      const submitDate = commonDateUtils.dateToMonthDayYearFormat(new Date());
      const curQuestion = reviewedToday.find(
        (question) => question.id === pulseQuestionId
      );

      const isSubmitValid = canSubmitPulse(submitDate, curQuestion);
      if (!isSubmitValid) {
        toast.show('This question is not available!');
        navigate(appUtils.getHomeRoute());
        return true;
      }

      const result = await submitPulseReview(data);
      if (!result || !result.success) {
        toast.error('Uh oh, we ran into an issue. Please try again later!');
        return false;
      }
      toast.show('Answer submitted');

      const newReviewedToday = curQuestion
        ? reviewedToday.map((question) => {
          if (question.id === pulseQuestionId) {
            return { ...question, date: submitDate };
          }
          return question;
        })
        : [...reviewedToday, { id: pulseQuestionId, date: submitDate }];
      localStorage.setItem(
        'pulse-shared-answered',
        JSON.stringify(newReviewedToday)
      );
      navigate(appUtils.getHomeRoute());
      return true;
    }
  };

  return (
    <PulseReviewSharedForm
      onSubmit={submit}
      headerOvertitle='Pulse Question shared with you'
      questionText={questionText}
      questionType={questionType}
      questionChoices={questionChoices}
      questionAnonymity={questionAnonymity}
      showAnonymityField={loggedUserId}
      isLoading={isFetching}
    />
  );
};

export default PulseReviewFromShare;
